import { useInView } from '../hooks/useInView';
import './OldAlexHubSection.css';

const PILLARS = [
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <path d="M11 2L3 6v5c0 5 3.6 9.7 8 11 4.4-1.3 8-6 8-11V6L11 2z"
              stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        <path d="M7.5 11l2.5 2.5 4.5-4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    accent: 'yellow',
    title: 'Built from Real Experience',
    body: 'TaxiOps was built from inside the transportation business, based on deep operational understanding of dispatch, drivers, vehicles, compliance, and the realities of running Taxi and NEMT operations every day.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <path d="M4 17L8 13 12 15 18 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        <rect x="3" y="3" width="16" height="16" rx="2.5" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    ),
    accent: 'blue',
    title: 'Disciplined Execution',
    body: 'Old Alex Hub develops technology solutions grounded in operational understanding and disciplined execution - delivering real value before expanding scope. TaxiOps reflects that discipline through focused implementation, careful rollout, and measured growth.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <circle cx="11" cy="11" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M11 7v4l3 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M6 17.5A8.5 8.5 0 0 1 11 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity=".3"/>
      </svg>
    ),
    accent: 'green',
    title: 'Long-Term Value Creation',
    body: 'Every product from Old Alex Hub is built for long-term value creation, not short-term growth. TaxiOps is built to serve transportation operations reliably and grow on a foundation of real operational performance.',
  },
];

const ACCENT_MAP = {
  yellow: { bg: 'var(--yellow-glow)', border: 'var(--border-yellow)', color: 'var(--yellow)' },
  blue:   { bg: 'var(--blue-glow)',   border: 'var(--border-blue)',   color: 'var(--blue-light)' },
  green:  { bg: 'var(--green-glow)',  border: 'rgba(16,185,129,0.25)', color: 'var(--green-light)' },
};

export default function OldAlexHubSection() {
  const [ref, inView] = useInView();

  return (
    <section className="hub-section" id="about" aria-labelledby="hub-heading">
      <div className="hub-section__divider" aria-hidden="true" />
      <div className="container" ref={ref}>
        {/* Label */}
        <p className={`section-label fade-up ${inView ? 'in-view' : ''}`}>
          About Old Alex Hub
        </p>

        {/* Heading */}
        <div className="hub-section__header">
          <h2
            id="hub-heading"
            className={`hub-section__heading fade-up d1 ${inView ? 'in-view' : ''}`}
          >
            An Official Business Venture of<br />
            <span className="gradient-text">Old Alex Hub</span>
          </h2>
          <div className={`hub-section__intro-block fade-up d2 ${inView ? 'in-view' : ''}`}>
            <p className="hub-section__intro">
              TaxiOps is a new official business venture of Old Alex Hub — created from real
              transportation experience and built to address real operational challenges
              facing Taxi and NEMT providers.
            </p>
            <p className="hub-section__intro">
              Old Alex Hub is a parent company for multiple businesses, products, and
              practical technology solutions built to solve real-world problems. TaxiOps
              is one of its newest official ventures.
            </p>
            <a
              href="mailto:info@oldalexhub.com?subject=Old Alex Hub General Inquiry"
              className="btn btn--outline hub-section__contact-btn"
            >
              Contact Old Alex Hub
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
                <path d="M2.5 7.5h10M9 4l3.5 3.5L9 11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          </div>
        </div>

        {/* Pillars */}
        <div className="hub-section__pillars">
          {PILLARS.map((p, i) => {
            const a = ACCENT_MAP[p.accent];
            return (
              <div
                key={p.title}
                className={`hub-pillar fade-up d${i + 2} ${inView ? 'in-view' : ''}`}
              >
                <div
                  className="hub-pillar__icon"
                  style={{ background: a.bg, border: `1px solid ${a.border}`, color: a.color }}
                  aria-hidden="true"
                >
                  {p.icon}
                </div>
                <h3 className="hub-pillar__title">{p.title}</h3>
                <p className="hub-pillar__body">{p.body}</p>
              </div>
            );
          })}
        </div>

        {/* Brand statement */}
        <div className={`hub-section__statement fade-up d5 ${inView ? 'in-view' : ''}`}>
          <h3 className="hub-statement__title">Practical Technology. Real Operational Impact.</h3>
          <div className="hub-statement__body">
            <p>
              Old Alex Hub builds technology solutions grounded in real-world operational
              understanding — not theoretical frameworks or consumer-first thinking. Every
              product is designed to address a real problem, serve real operators, and
              deliver measurable value from day one of deployment.
            </p>
            <p>
              TaxiOps reflects that philosophy. It was not designed for a market segment.
              It was designed for the specific operational reality of Taxi and NEMT providers
              who run fleets, manage drivers, coordinate dispatch, and execute thousands of
              trips with accountability for every one.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
